import { FieldArray, FormikContextType, FormikProvider } from 'formik';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { OnChangeValue } from 'react-select';

import { ThemeColors } from 'models/general';
import { GenderTypes } from 'utils/enums/common';
import setFieldProps from 'utils/setFieldProps';
import MDatePicker from 'views/MDatePicker/MDatePicker';
import { InputType } from 'views/MDatePicker/MDatePicker.model';
import MFormCard from 'views/MForm/MFormCard/MFormCard';
import MFormContainer from 'views/MForm/MFormContainer/MFormContainer';
import MFormContent from 'views/MForm/MFormContent/MFormContent';
import MFormFieldWrap from 'views/MForm/MFormFieldWrap/MFormFieldWrap';
import MFormInput from 'views/MForm/MFormFields/MFormInput/MFormInput';
import MFormSelect from 'views/MForm/MFormFields/MFormSelect/MFormSelect';
import MFormSimpleSelect from 'views/MForm/MFormFields/MFormSimpleSelect/MFormSimpleSelect';
import MFormFooter from 'views/MForm/MFormFooter/MFormFooter';
import { MSelectChangeData, MSelectOption } from 'views/MSelect/MSelect.model';
import MSwitch from 'views/MSwitch/MSwitch';

import { IUserProfilePersonalInfoFormFields } from './PersonalInfoTab.model';
import classes from './PersonalInfoTab.module.scss';

type Props<Fields extends IUserProfilePersonalInfoFormFields> = {
  formInstance: FormikContextType<Fields>;
  isLoading: boolean;
  isCompanyAdmin: boolean;
  genderOptions: MSelectOption<null, GenderTypes>[];
};

const PersonalInfoTabView = <Fields extends IUserProfilePersonalInfoFormFields>(
  props: React.PropsWithChildren<Props<Fields>>,
): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className={classes['container']}>
      <MFormContainer title={t('userProfile.tabs.personalInfo')} className={classes['formContainer']}>
        <MFormContent formik={props.formInstance} isLoading={props.isLoading} className={classes['contentContainer']}>
          <MFormCard header={t('userProfile.form.personalDetails.title')}>
            <div className={classes['personalDetailsSubHeader']}>
              <div>{t('userProfile.form.personalDetails.notice')}</div>
              <span className={classes['avatarName']}>
                {`${props.formInstance.values.firstName} ${props.formInstance.values.lastName}`
                  .split(' ')
                  .slice(0, 2)
                  .map((word: string) => word.charAt(0))
                  .join('')
                  .toUpperCase()}
              </span>
            </div>
            <div className={classes['fields']}>
              <MFormFieldWrap name="firstName" displayName={t('userProfile.form.personalDetails.firstName')}>
                <MFormInput {...setFieldProps(props.formInstance, 'firstName')} />
              </MFormFieldWrap>
              <MFormFieldWrap name="lastName" displayName={t('userProfile.form.personalDetails.lastName')}>
                <MFormInput {...setFieldProps(props.formInstance, 'lastName')} />
              </MFormFieldWrap>
              <MFormFieldWrap name="phone" displayName={t('userProfile.form.personalDetails.phone')}>
                <MFormInput {...setFieldProps(props.formInstance, 'phone')} type="tel" autoComplete="phone" />
              </MFormFieldWrap>
              <MFormFieldWrap name="email" displayName={t('userProfile.form.personalDetails.email')}>
                <MFormInput {...setFieldProps(props.formInstance, 'email')} disabled />
              </MFormFieldWrap>
              <MFormFieldWrap name="gender" displayName={t('userProfile.form.personalDetails.gender')}>
                <MFormSelect
                  {...setFieldProps<
                    Fields,
                    GenderTypes,
                    [OnChangeValue<MSelectChangeData<null, GenderTypes>, false>],
                    GenderTypes,
                    GenderTypes | null
                  >(props.formInstance, 'gender', {
                    beforeChange: (event: OnChangeValue<MSelectChangeData<null, GenderTypes>, false>) => event?.id || null,
                  })}
                  options={props.genderOptions}
                ></MFormSelect>
              </MFormFieldWrap>
              <MFormFieldWrap name="dateOfBirth" displayName={t('userProfile.form.personalDetails.dateOfBirth')}>
                <MDatePicker
                  hideActions
                  fromYear={moment().subtract(120, 'year').year()}
                  toYear={moment().year()}
                  dropdown
                  clearable
                  closeOnSelection
                  position="bottom"
                  inputType={InputType.Date}
                  value={props.formInstance.values.dateOfBirth || undefined}
                  onChange={(date?: moment.Moment) => props.formInstance.setFieldValue('dateOfBirth', date)}
                ></MDatePicker>
              </MFormFieldWrap>
            </div>
          </MFormCard>

          <MFormCard header={t('userProfile.form.userRoles.title')}>
            {props.isCompanyAdmin ? (
              <MFormFieldWrap
                className={classes['switch']}
                name="isCompanyAdmin"
                displayName={t('userProfile.form.userRoles.companyAdmin')}
              >
                <MSwitch
                  checked={props.formInstance.values.isCompanyAdmin}
                  onChange={() => null}
                  color={ThemeColors.Info}
                  disabled
                ></MSwitch>
              </MFormFieldWrap>
            ) : null}
            {props.formInstance.values.isCompanyAdmin ? null : (
              <FormikProvider value={props.formInstance}>
                <FieldArray
                  name="access"
                  render={() => (
                    <div>
                      {props.formInstance.values.access?.map((access: { role: string; site: string }, i: number) => {
                        return (
                          <div key={`${access.role}-${i}`} className={classes['fields-with-button']}>
                            <MFormFieldWrap name="site" displayName={t('userProfile.form.userRoles.site')}>
                              <MFormSimpleSelect
                                {...setFieldProps<Fields, string, [React.ChangeEvent<HTMLSelectElement>]>(
                                  props.formInstance,
                                  `access.${i}.site`,
                                )}
                                options={[{ value: access.site, label: access.site }]}
                                value={access.site}
                                disabled
                              ></MFormSimpleSelect>
                            </MFormFieldWrap>

                            <MFormFieldWrap name="role" displayName={t('userProfile.form.userRoles.role')}>
                              <MFormSimpleSelect
                                {...setFieldProps<Fields, string, [React.ChangeEvent<HTMLSelectElement>]>(
                                  props.formInstance,
                                  `access.${i}.role`,
                                )}
                                options={[{ value: access.role, label: access.role }]}
                                value={access.role}
                                disabled
                              ></MFormSimpleSelect>
                            </MFormFieldWrap>
                          </div>
                        );
                      })}
                    </div>
                  )}
                ></FieldArray>
              </FormikProvider>
            )}
          </MFormCard>
          <MFormFooter confirmText={t('userProfile.form.update')}></MFormFooter>
        </MFormContent>
      </MFormContainer>
    </div>
  );
};

PersonalInfoTabView.displayName = 'PersonalInfoTab';
PersonalInfoTabView.defaultProps = {};

export default PersonalInfoTabView;
